import React, { Fragment, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import {
  getAssociateVideoQuestions,
  deleteVideoAssociateQuestion,
} from "../../../api/videos.service";
import { IQuestion } from "../../../model/interface/question";
import { IStandardQuestion } from "../../../model/interface/standardQuestion";
import Constant from "../../../utils/constant";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Loader from "../../shared/loader/loader";
import {
  DeleteIcon,
  HumanIcon,
  MachineIcon,
  SpanishTransIcon,
  TextIcon,
  VideoLegendIcon,
  AddStandardIcon,
  DiagnosticIcon,
  ApproveIcon,
  Disapprove,
  CopyIcon,
} from "../../../assets/icons/index";
import { toast } from "react-toastify";
import { getSubjectAPI } from "../../../api/subject.service";
import DeleteModal from "../../shared/common/deleteModal";
import Tooltip from "../../shared/common/ellipsisText";
import Copy from "../../shared/common/copy";

interface AssociatedQuestionProps {
  videoId: number;
  videoData: any;
  setAssociateQuestionIds: any;
  associateQuestionIds: string[];
}

function AssociatedQuestion(props: AssociatedQuestionProps) {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const { videoId, videoData, setAssociateQuestionIds, associateQuestionIds } =
    props;
  const navigate = useNavigate();
  let defaultStandardQuestion: IStandardQuestion = {
    questionId: 0,
    questionText: "",
    domainId: 0,
    domainName: "",
    standardCode: "",
    standardId: 0,
    standardName: "",
    subjectId: 0,
    subjectName: "",
  };
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [associateQuestionId, setAssociateQuestionId] = useState<number>();
  const [associateEnglishQuestionId, setAssociateEnglishQuestionId] =
    useState<string>();
  const [associateSpanishQuestionId, setAssociateSpanishQuestionId] =
    useState<string>();

  useEffect(() => {
    if (isLoaded) {
      bindAssociatedQuestion();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      bindAssociatedQuestion();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const bindAssociatedQuestion = async () => {
    setLoading(true);
    const response = getAssociateVideoQuestions(videoId, pageNumber, pageSize);
    const data = (await response).data;
    setLoading(false);
    setQuestions(data.data);
    const videoIds: string[] =
      associateQuestionIds.length == 0 ? [] : associateQuestionIds;
    data.data.map((video, index) => {
      videoIds.push(video.englishQuestionReferenceId);
      videoIds.push(video.spanishQuestionReferenceId);
    });
    setAssociateQuestionIds(videoIds);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
  };

  const handleAddQuestionRedirect = (type: string) => {
    defaultStandardQuestion = {
      domainId: videoData.domainId,
      domainName: videoData.domainName,
      subjectId: videoData.subjectId,
      subjectName: videoData.subjectName,
      standardId: videoData.standardId,
      standardCode: videoData.standardCode,
      standardName: videoData.standardName,
      questionText: "",
      questionId: 0,
    };

    if (type == "associate") {
      navigate(Constant.VIDEO_ASSOCIATED_QUESTIONS, {
        state: {
          associateVideoId: videoId,
          associateVideoName: videoData.title,
          englishVideoRefId: videoData.englishVideoReferenceId,
          spanishVideoRefId: videoData.spanishVideoReferenceId,
          associateStandardData: defaultStandardQuestion,
        },
      });
    } else {
      navigate(Constant.AUTHORQUESTION, {
        state: {
          associateVideoId: videoId,
          associatedEnglishVideoRefId: videoData.englishVideoReferenceId,
          associatedSpanishVideoRefId: videoData.spanishVideoReferenceId,
          associateStandardData: defaultStandardQuestion,
          associateStandardId: videoData.standardId,
        },
      });
    }
  };

  const deleteAssociateQuestion = (
    questionId,
    englishQuestionReferenceId,
    spanishQuestionReferenceId
  ) => {
    setShowDeleteModal(true);
    setAssociateQuestionId(questionId);
    setAssociateEnglishQuestionId(englishQuestionReferenceId);
    setAssociateSpanishQuestionId(spanishQuestionReferenceId);
  };

  const deleteRecordHandler = () => {
    setLoading(true);
    deleteVideoAssociateQuestion(
      videoId,
      associateQuestionId,
      0,
      associateEnglishQuestionId,
      associateSpanishQuestionId,
      videoData.englishVideoReferenceId,
      videoData.spanishVideoReferenceId
    ).then((res) => {
      setShowDeleteModal(false);
      bindAssociatedQuestion();
      setLoading(false);
      toast.success("Associated question deleted successfully");
    });
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleRedirect = async (
    objectType: string,
    objectId: string,
    subjectType?: number
  ) => {
    if (objectType === "QuestionId" || objectType === "QuestionText") {
      navigate(Constant.MANAGEQUESTION.replace(":id", objectId), {
        state: { questionIdToEdit: objectId },
      });
    } else if (objectType === "Subject") {
      if (subjectType === Constant.SubjectType.LO) {
        navigate(Constant.LIFTOFFSUBJECTEDIT.replace(":id", objectId));
      } else if (objectId && parseInt(objectId) > 0) {
        try {
          const isValidSubjectId = await getSubjectAPI(objectId);
          if (isValidSubjectId) {
            navigate(Constant.SUBJECTDETAIL.replace(":id", objectId));
          } else {
            toast.warn("Question id not found");
          }
        } catch (e) {
          toast.error("Something went wrong");
        }
      } else {
        navigate(Constant.ADDSUBJECT);
      }
    } else if (objectType === "Domain") {
      navigate(Constant.EDITDOMAIN.replace(":id", objectId));
    } else if (objectType === "Standard") {
      navigate(Constant.EDITSTANDARD.replace(":id", objectId));
    }
  };

  return (
    <Fragment>
      {loading && <Loader />}
      {showDeleteModal && (
        <DeleteModal
          closeDeleteModal={closeDeleteModal}
          deleteRecord={deleteRecordHandler}
        />
      )}

      <div className="text-gray-900 block px-4 py-4 text-sm shadow border-b border-gray-200 rounded-b-lg">
        <div className="flex w-full mt-1 mb-4 rounded-lg gap-2 items-center">
          <button
            onClick={() => handleAddQuestionRedirect("associate")}
            type="button"
            className="flex items-center text-sm cursor-pointer"
          >
            <AddStandardIcon />
            Associate Question
          </button>
          <button
            onClick={() => handleAddQuestionRedirect("add")}
            type="button"
            className="flex items-center text-sm cursor-pointer"
          >
            <AddStandardIcon />
            Add Question
          </button>
        </div>
        {questions && (
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary-color">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Id</span>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Question Text</span>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Type</span>
                  </th>

                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Subject</span>
                  </th>

                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Domain</span>
                  </th>

                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Standard</span>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">DOK</span>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white"
                  >
                    <span className="flex gap-2">Exp</span>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 font-light whitespace-wrap text-sm text-center text-white "
                  >
                    <SpanishTransIcon className="text-white fill-current h-4 w-4 dark:text-white" />
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider "
                  ></th>
                  <th
                    scope="col"
                    className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                  >
                    <span className="flex">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {questions &&
                  questions?.map((question, index) => (
                    <tr key={index} className="bg-white">
                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        <div className="flex gap-2">
                          <CopyIcon
                            className="h-5 cursor-pointer"
                            title="Copy id"
                            onClick={() => Copy([question.questionId])}
                          />
                          <a
                            className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                            onClick={(e) => {
                              handleRedirect(
                                "QuestionId",
                                question?.questionId
                                  ? question.questionId.toString()
                                  : ""
                              );
                            }}
                          >
                            {question.questionId}
                          </a>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        <a
                          className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                          onClick={(e) => {
                            handleRedirect(
                              "QuestionText",
                              question?.questionId
                                ? question.questionId.toString()
                                : ""
                            );
                          }}
                        >
                          <Tooltip text={question.questionText} length={35} />
                        </a>
                      </td>
                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        {question.questionTypeName}
                      </td>

                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        <a
                          className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                          onClick={(e) => {
                            handleRedirect(
                              "Subject",
                              question?.subjectId
                                ? question.subjectId.toString()
                                : "",
                              question.subjectTypeId
                            );
                          }}
                        >
                          {question.subjectName}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        <a
                          className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                          onClick={(e) => {
                            handleRedirect(
                              "Domain",
                              question?.domainId
                                ? question.domainId.toString()
                                : ""
                            );
                          }}
                        >
                          {question.domainName}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        <a
                          className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                          onClick={(e) => {
                            handleRedirect(
                              "Standard",
                              question?.standardId
                                ? question.standardId.toString()
                                : ""
                            );
                          }}
                        >
                          [{question.standardCode}] {question.standardName}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                        {question.dok}
                      </td>
                      <td className="px-3 py-2 whitespace-wrap text-sm text-center font-normal">
                        <div className="flex items-center">
                          {question.settings?.filter((e) => {
                            return e.videoExplanation != undefined;
                          })[0]?.videoExplanation && (
                            <span className="relative w-11 inline-block">
                              <VideoLegendIcon title="Video explanation available for this subject" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                        {question.isHumanTranslated ? (
                          <HumanIcon
                            title="Human Translated"
                            className="w-4 h-4"
                          />
                        ) : question.isMachineTranslated ? (
                          <MachineIcon
                            title="Machine Translated"
                            className="w-4 h-4"
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                        <div className="flex space-x-2 justify-center items-center">
                          {question.isDiagnostics && <DiagnosticIcon />}
                          {question.isPublished ? (
                            <ApproveIcon title="Approved" />
                          ) : (
                            <Disapprove />
                          )}
                        </div>
                      </td>
                      <td className="flex justify-center items-center px-3 py-2 text-sm text-center font-normal text-gray-500">
                        <DeleteIcon
                          onClick={() =>
                            deleteAssociateQuestion(
                              question.questionId,
                              question.englishQuestionReferenceId,
                              question.spanishQuestionReferenceId
                            )
                          }
                          className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        <div
          className={`flex justify-between items-center ${
            questions.length === 0 ? "hidden" : ""
          }`}
        >
          <Pagination
            pages={totalPages}
            setPageNumber={setPageNumber}
            ref={paginationRef}
          />
          <span>
            <span className="text-sm pr-4 ml-auto text-left-700">
              Showing {pageNumber} to{" "}
              {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
              {totalRecords} results
            </span>

            <SelectPageSize
              setPageSize={setPageSize}
              type={"second"}
              ref={pageSizeRef}
            />
          </span>
        </div>

        {questions.length === 0 && <p className="p-2">No Records Found</p>}
      </div>
    </Fragment>
  );
}

export default AssociatedQuestion;
